import type {
    QuestionnaireSwapOffsetConstantType,
    QuestionnaireSwapStepConstantType,
    QuestionnaireKeyboardEventType,
    QuestionnaireSwapStep,
    QuestionnaireAnswers,
    QuestionnaireAnswer,
    IQuestionnaireItem
} from "./type";
import {
    getDirectionSign
} from "../../helpers/direction";

export const QUESTIONNAIRE_EMPTY_ITEMS: Array<IQuestionnaireItem> = [];

export const QUESTIONNAIRE_EMPTY_ANSWERS: QuestionnaireAnswers = {};

export const QUESTIONNAIRE_TEXT_TAGS: Array<string> = [
    "textarea",
    "input"
];

export const QUESTIONNAIRE_BUTTON_TAG = "button";

export const QUESTIONNAIRE_ENTER_KEY = "Enter";

export const QUESTIONNAIRE_TOGGLE_KEY = " ";

export const QUESTIONNAIRE_ARROW_STEPS: Record<string, 1 | -1> = {
    ArrowDown: 1,
    ArrowUp: -1
};

export const getQuestionnaireAnswerList = (answer?: QuestionnaireAnswer): Array<string> => {
    if(Array.isArray(answer)) {
        return answer.filter((value) => value.length > 0);
    }

    return answer ? [
        answer
    ] : [];
};

export const getQuestionnaireIsAnswered = (answer?: QuestionnaireAnswer): boolean => {
    return getQuestionnaireAnswerList(answer).some((value) => value.trim().length > 0);
};

export const getQuestionnaireSelectBehaviour = (item: IQuestionnaireItem) => {
    return item.selectBehaviour ?? "single";
};

export const getQuestionnaireChoiceValues = (item: IQuestionnaireItem): Array<string> => {
    return (item.choices ?? []).map((choice) => choice.value);
};

export const getQuestionnaireSelectedValues = ({
    answer,
    item
}: {
    answer?: QuestionnaireAnswer;
    item: IQuestionnaireItem;
}): Array<string> => {
    const choiceValues = getQuestionnaireChoiceValues(item);

    return getQuestionnaireAnswerList(answer).filter((value) => choiceValues.includes(value));
};

export const getQuestionnaireInputText = ({
    answer,
    item
}: {
    answer?: QuestionnaireAnswer;
    item: IQuestionnaireItem;
}): string => {
    const choiceValues = getQuestionnaireChoiceValues(item);

    return getQuestionnaireAnswerList(answer).find((value) => !choiceValues.includes(value)) ?? "";
};

export const getQuestionnaireNextAnswer = ({
    answer,
    value,
    item
}: {
    answer?: QuestionnaireAnswer;
    item: IQuestionnaireItem;
    value: string;
}): QuestionnaireAnswer => {
    if(getQuestionnaireSelectBehaviour(item) !== "multiple") {
        return value;
    }

    const selectedValues = getQuestionnaireSelectedValues({
        answer,
        item
    });

    const text = getQuestionnaireInputText({
        answer,
        item
    });

    const nextValues = selectedValues.includes(value) ? selectedValues.filter((selectedValue) => selectedValue !== value) : [
        ...selectedValues,
        value
    ];

    return text ? [
        ...nextValues,
        text
    ] : nextValues;
};

export const getQuestionnaireTextAnswer = ({
    answer,
    text,
    item
}: {
    answer?: QuestionnaireAnswer;
    item: IQuestionnaireItem;
    text: string;
}): QuestionnaireAnswer => {
    if(getQuestionnaireSelectBehaviour(item) !== "multiple") {
        return text;
    }

    const selectedValues = getQuestionnaireSelectedValues({
        answer,
        item
    });

    return text ? [
        ...selectedValues,
        text
    ] : selectedValues;
};

export const getQuestionnaireReviewText = ({
    answer,
    item
}: {
    answer?: QuestionnaireAnswer;
    item: IQuestionnaireItem;
}): string => {
    const choices = item.choices ?? [];

    return getQuestionnaireAnswerList(answer).map((value) => choices.find((choice) => choice.value === value)?.title ?? value).join(", ");
};

export const getQuestionnaireCleanAnswers = ({
    answers,
    items
}: {
    items: Array<IQuestionnaireItem>;
    answers: QuestionnaireAnswers;
}): QuestionnaireAnswers => {
    const cleanAnswers: QuestionnaireAnswers = {};

    items.forEach((item) => {
        const answer = answers[item.name];

        if(answer !== undefined && getQuestionnaireIsAnswered(answer)) {
            cleanAnswers[item.name] = answer;
        }
    });

    return cleanAnswers;
};

export const getQuestionnaireTagName = (target: unknown): string => {
    if(typeof target === "object" && target !== null && "tagName" in target && typeof target.tagName === "string") {
        return target.tagName.toLowerCase();
    }

    return "";
};

export const getQuestionnaireHasModifier = (event: Pick<QuestionnaireKeyboardEventType, "ctrlKey" | "metaKey" | "altKey">): boolean => {
    return !!(event.ctrlKey || event.metaKey || event.altKey);
};

export const getQuestionnaireChoiceShortcut = ({
    choice,
    index
}: {
    choice: NonNullable<IQuestionnaireItem["choices"]>[number];
    index: number;
}): string => {
    return choice.shortcut ?? String(index + 1);
};

export const getQuestionnaireNextIndex = ({
    index,
    item,
    step
}: {
    item: IQuestionnaireItem;
    index: number;
    step: 1 | -1;
}): number => {
    const choices = item.choices ?? [];

    for(let offset = 1; offset <= choices.length; offset++) {
        const nextIndex = (index + step * offset + choices.length * offset) % choices.length;

        if(!choices[nextIndex]?.isDisabled) {
            return nextIndex;
        }
    }

    return index;
};

export const getQuestionnaireSwapStep = ({
    previousIndex,
    index
}: QuestionnaireSwapStepConstantType): QuestionnaireSwapStep => {
    if(index === previousIndex) {
        return 0;
    }

    return index > previousIndex ? 1 : -1;
};

export const getQuestionnaireSwapOffset = ({
    direction,
    spaces,
    step
}: QuestionnaireSwapOffsetConstantType): number => {
    return step * getDirectionSign({
        direction
    }) * spaces.spacingSm;
};

export const getQuestionnaireIsEnterEvent = (event: unknown): boolean => {
    return typeof event === "object" && event !== null && Reflect.get(event, "key") === QUESTIONNAIRE_ENTER_KEY;
};

export const getQuestionnaireIsPlainEnterEvent = (event: unknown): boolean => {
    if(!getQuestionnaireIsEnterEvent(event) || typeof event !== "object" || event === null) {
        return false;
    }

    const nativeEvent = Reflect.get(event, "nativeEvent");

    const isComposing = typeof nativeEvent === "object" && nativeEvent !== null && !!Reflect.get(nativeEvent, "isComposing");

    return !isComposing && ![
        "shiftKey",
        "ctrlKey",
        "metaKey",
        "altKey"
    ].some((name) => !!Reflect.get(event, name));
};
